import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LOGO_URL } from '@/lib/brand';

const Terms = () => {
    const lastUpdated = 'December 19, 2025';
    const effectiveDate = 'December 19, 2025';

    return (
        <div className="min-h-screen bg-[#0a0a0f] text-white">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0f]/80 backdrop-blur-lg border-b border-white/5">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <img src={LOGO_URL} alt="Shiro Security" className="w-10 h-10 object-contain" />
                        <span className="text-xl font-bold gradient-text">SHIRO</span>
                        <span className="text-xl font-light text-white/80">Security</span>
                    </Link>
                    <Link to="/">
                        <Button variant="ghost" className="text-white/70 hover:text-white hover:bg-white/5">
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Back to Home
                        </Button>
                    </Link>
                </div>
            </nav>

            {/* Content */}
            <div className="pt-32 pb-20 px-6">
                <div className="max-w-4xl mx-auto">
                    <div className="mb-12">
                        <h1 className="text-4xl font-bold mb-4">Master Services Agreement</h1>
                        <p className="text-white/60">Terms of Service for Shiro Security Platform</p>
                        <p className="text-white/60 mt-2">Effective Date: {effectiveDate} | Last Updated: {lastUpdated}</p>
                    </div>

                    {/* Important Notice */}
                    <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-6 mb-8">
                        <div className="flex items-start gap-4">
                            <AlertTriangle className="w-6 h-6 text-orange-400 flex-shrink-0 mt-1" />
                            <div>
                                <h3 className="font-bold text-orange-400 mb-2">IMPORTANT NOTICE</h3>
                                <p className="text-white/80 text-sm">
                                    PLEASE READ THIS MASTER SERVICES AGREEMENT ("AGREEMENT") CAREFULLY BEFORE USING THE SHIRO SECURITY SERVICES. BY ACCESSING OR USING OUR SERVICES, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THIS AGREEMENT. IF YOU DO NOT AGREE TO THESE TERMS, DO NOT USE OUR SERVICES.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="prose prose-invert max-w-none space-y-8">
                        <section className="glass-card rounded-xl p-8">
                            <h2 className="text-2xl font-bold mb-4 text-cyan-400">1. Definitions</h2>
                            <ul className="text-white/80 space-y-3">
                                <li><strong>"Agreement"</strong> means this Master Services Agreement, including all exhibits, schedules, and amendments.</li>
                                <li><strong>"Client," "You," or "Your"</strong> means the entity or individual that has agreed to this Agreement.</li>
                                <li><strong>"Shiro Security," "We," "Us," or "Our"</strong> means Shiro Security, LLC.</li>
                                <li><strong>"Services"</strong> means the AI-powered cybersecurity monitoring, threat detection, and response services provided by Shiro Security.</li>
                                <li><strong>"AI Agents"</strong> means the automated artificial intelligence systems including Watchdog (detection), Investigator (analysis), and Jurist (compliance) that comprise the Shiro Security platform.</li>
                                <li><strong>"Protected Assets"</strong> means the Client's devices, systems, networks, and cloud services monitored by the Services.</li>
                                <li><strong>"Security Event"</strong> means any detected anomaly, threat, or incident identified by the Services.</li>
                            </ul>
                        </section>

                        <section className="glass-card rounded-xl p-8 border-2 border-cyan-500/30">
                            <h2 className="text-2xl font-bold mb-4 text-cyan-400">2. Shared Responsibility Model</h2>
                            <div className="bg-cyan-500/10 rounded-lg p-4 mb-4">
                                <p className="text-white font-semibold">
                                    CRITICAL: THE CLIENT ACKNOWLEDGES AND AGREES THAT THE AI AGENTS ACT AS AN AUTOMATED EXTENSION OF THE CLIENT'S OWN IT TEAM. THE CLIENT ASSUMES ULTIMATE RESPONSIBILITY FOR THE CONFIGURATION, AUTHORIZATION LEVELS, AND ACTIONS GRANTED TO THE AI AGENTS.
                                </p>
                            </div>
                            
                            <h3 className="text-xl font-semibold mb-3 text-white">2.1 Shiro Security Responsibilities</h3>
                            <ul className="list-disc list-inside text-white/80 space-y-2 mb-4">
                                <li>Provide commercially reasonable AI-powered security monitoring services</li>
                                <li>Maintain the availability and functionality of the platform</li>
                                <li>Implement industry-standard security practices for our infrastructure</li>
                                <li>Provide timely threat detection and alerting based on configured parameters</li>
                                <li>Maintain audit logs of all AI Agent actions and decisions</li>
                            </ul>

                            <h3 className="text-xl font-semibold mb-3 text-white">2.2 Client Responsibilities</h3>
                            <ul className="list-disc list-inside text-white/80 space-y-2 mb-4">
                                <li>Properly configure and maintain Protected Assets</li>
                                <li>Define and authorize the scope of AI Agent actions</li>
                                <li>Provide final human approval for critical security actions when required</li>
                                <li>Maintain accurate contact information for security alerts</li>
                                <li>Review and respond to security notifications in a timely manner</li>
                                <li>Ensure compliance with applicable laws and regulations</li>
                                <li>Maintain appropriate cyber insurance coverage</li>
                            </ul>

                            <div className="bg-white/5 rounded-lg p-4">
                                <p className="text-white/80 text-sm">
                                    <strong>Analogy:</strong> The AI Agents function as automated security tools under the Client's direction. Similar to how a power tool manufacturer is not liable for how a contractor uses the tool, Shiro Security provides the technology while the Client directs its application within their environment.
                                </p>
                            </div>
                        </section>

                        <section className="glass-card rounded-xl p-8 border-2 border-orange-500/30">
                            <h2 className="text-2xl font-bold mb-4 text-orange-400">3. No Guarantee of Security</h2>
                            <div className="bg-orange-500/10 rounded-lg p-4 mb-4">
                                <p className="text-white font-semibold">
                                    SHIRO SECURITY PROVIDES COMMERCIALLY REASONABLE MONITORING AND THREAT DETECTION SERVICES. HOWEVER, WE DO NOT AND CANNOT GUARANTEE 100% IMMUNITY FROM CYBER ATTACKS, DATA BREACHES, OR SECURITY INCIDENTS.
                                </p>
                            </div>
                            <p className="text-white/80 leading-relaxed mb-4">
                                The Client acknowledges and understands that:
                            </p>
                            <ul className="list-disc list-inside text-white/80 space-y-2">
                                <li>No security solution can prevent all cyber threats</li>
                                <li>New and zero-day vulnerabilities may evade detection</li>
                                <li>The effectiveness of the Services depends on proper configuration and Client cooperation</li>
                                <li>Security is a shared responsibility requiring ongoing vigilance by all parties</li>
                                <li>The Services are designed to reduce risk, not eliminate it entirely</li>
                            </ul>
                        </section>

                        <section className="glass-card rounded-xl p-8 border-2 border-purple-500/30">
                            <h2 className="text-2xl font-bold mb-4 text-purple-400">4. Human-in-the-Loop Authorization</h2>
                            <p className="text-white/80 leading-relaxed mb-4">
                                For critical security actions that may significantly impact business operations, the Services implement a "Human-in-the-Loop" authorization protocol:
                            </p>
                            
                            <h3 className="text-xl font-semibold mb-3 text-white">4.1 Critical Actions Requiring Human Approval</h3>
                            <ul className="list-disc list-inside text-white/80 space-y-2 mb-4">
                                <li>Isolation or shutdown of production servers</li>
                                <li>Blocking of executive or VIP user accounts</li>
                                <li>Network-wide policy changes</li>
                                <li>Data quarantine or deletion actions</li>
                                <li>Any action designated as "Critical" by the Client's configuration</li>
                            </ul>

                            <h3 className="text-xl font-semibold mb-3 text-white">4.2 Authorization Process</h3>
                            <p className="text-white/80 leading-relaxed mb-4">
                                When a critical action is recommended by the AI Agents, the designated Client contact will receive notification via the configured channels (voice call, SMS, email, or dashboard alert). The action will only proceed upon explicit human authorization.
                            </p>

                            <div className="bg-purple-500/10 rounded-lg p-4">
                                <p className="text-white/80 text-sm">
                                    <strong>Legal Significance:</strong> By providing human authorization for critical actions, the Client assumes responsibility for those actions. Shiro Security maintains an immutable audit log of all authorization requests and responses.
                                </p>
                            </div>
                        </section>

                        <section className="glass-card rounded-xl p-8 border-2 border-red-500/30">
                            <h2 className="text-2xl font-bold mb-4 text-red-400">5. Limitation of Liability</h2>
                            
                            <h3 className="text-xl font-semibold mb-3 text-white">5.1 Liability Cap</h3>
                            <div className="bg-red-500/10 rounded-lg p-4 mb-4">
                                <p className="text-white font-semibold">
                                    TO THE MAXIMUM EXTENT PERMITTED BY LAW, SHIRO SECURITY'S TOTAL AGGREGATE LIABILITY ARISING OUT OF OR RELATED TO THIS AGREEMENT SHALL NOT EXCEED THE AMOUNTS PAID BY THE CLIENT TO SHIRO SECURITY IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
                                </p>
                            </div>

                            <h3 className="text-xl font-semibold mb-3 text-white">5.2 Exclusion of Damages</h3>
                            <p className="text-white/80 leading-relaxed mb-4">
                                IN NO EVENT SHALL SHIRO SECURITY BE LIABLE FOR:
                            </p>
                            <ul className="list-disc list-inside text-white/80 space-y-2 mb-4">
                                <li>Indirect, incidental, special, consequential, or punitive damages</li>
                                <li>Loss of profits, revenue, data, or business opportunities</li>
                                <li>Cost of procurement of substitute services</li>
                                <li>Damages arising from unauthorized access or data breaches not caused by Shiro Security's gross negligence</li>
                                <li>Damages resulting from Client's failure to follow security recommendations</li>
                                <li>Damages arising from Client-authorized actions by the AI Agents</li>
                            </ul>

                            <h3 className="text-xl font-semibold mb-3 text-white">5.3 Exceptions</h3>
                            <p className="text-white/80 leading-relaxed">
                                The limitations in this section shall not apply to: (a) breaches of confidentiality obligations; (b) indemnification obligations; (c) gross negligence or willful misconduct; or (d) liability that cannot be limited under applicable law.
                            </p>
                        </section>

                        <section className="glass-card rounded-xl p-8">
                            <h2 className="text-2xl font-bold mb-4 text-cyan-400">6. Indemnification</h2>
                            
                            <h3 className="text-xl font-semibold mb-3 text-white">6.1 Client Indemnification</h3>
                            <p className="text-white/80 leading-relaxed mb-4">
                                The Client agrees to indemnify, defend, and hold harmless Shiro Security from any claims, damages, losses, and expenses (including reasonable attorneys' fees) arising from:
                            </p>
                            <ul className="list-disc list-inside text-white/80 space-y-2 mb-4">
                                <li>Client's use of the Services in violation of this Agreement</li>
                                <li>Client's violation of applicable laws or third-party rights</li>
                                <li>Actions taken by AI Agents as authorized by the Client</li>
                                <li>Client's failure to implement recommended security measures</li>
                                <li>Inaccurate or incomplete information provided by the Client</li>
                            </ul>

                            <h3 className="text-xl font-semibold mb-3 text-white">6.2 Shiro Security Indemnification</h3>
                            <p className="text-white/80 leading-relaxed">
                                Shiro Security agrees to indemnify and defend the Client from third-party claims alleging that the Services infringe any valid patent, copyright, or trademark, provided the Client promptly notifies Shiro Security of such claims and cooperates in the defense.
                            </p>
                        </section>

                        <section className="glass-card rounded-xl p-8">
                            <h2 className="text-2xl font-bold mb-4 text-cyan-400">7. Service Level Agreement</h2>
                            
                            <h3 className="text-xl font-semibold mb-3 text-white">7.1 Availability</h3>
                            <p className="text-white/80 leading-relaxed mb-4">
                                Shiro Security targets 99.9% uptime for the monitoring platform, excluding scheduled maintenance and circumstances beyond our reasonable control.
                            </p>

                            <h3 className="text-xl font-semibold mb-3 text-white">7.2 Response Times</h3>
                            <ul className="list-disc list-inside text-white/80 space-y-2 mb-4">
                                <li><strong>Critical Threats:</strong> Detection and alerting within seconds</li>
                                <li><strong>High Priority:</strong> Investigation initiated within 5 minutes</li>
                                <li><strong>Medium Priority:</strong> Review within 1 hour</li>
                                <li><strong>Low Priority:</strong> Included in daily reports</li>
                            </ul>

                            <h3 className="text-xl font-semibold mb-3 text-white">7.3 Sole Remedy</h3>
                            <p className="text-white/80 leading-relaxed">
                                For failure to meet the availability target, Client's sole remedy shall be service credits as outlined in the applicable service plan documentation.
                            </p>
                        </section>

                        <section className="glass-card rounded-xl p-8">
                            <h2 className="text-2xl font-bold mb-4 text-cyan-400">8. Term and Termination</h2>
                            
                            <h3 className="text-xl font-semibold mb-3 text-white">8.1 Term</h3>
                            <p className="text-white/80 leading-relaxed mb-4">
                                This Agreement begins on the Effective Date and continues for the subscription term selected by the Client, automatically renewing for successive periods unless either party provides written notice of non-renewal at least 30 days before the end of the current term.
                            </p>

                            <h3 className="text-xl font-semibold mb-3 text-white">8.2 Termination for Cause</h3>
                            <p className="text-white/80 leading-relaxed mb-4">
                                Either party may terminate this Agreement upon written notice if the other party materially breaches this Agreement and fails to cure such breach within 30 days of receiving written notice.
                            </p>

                            <h3 className="text-xl font-semibold mb-3 text-white">8.3 Effect of Termination</h3>
                            <p className="text-white/80 leading-relaxed">
                                Upon termination, Client's access to the Services will cease. Shiro Security will retain audit logs as required by law or this Agreement. Sections relating to Limitation of Liability, Indemnification, and Governing Law shall survive termination.
                            </p>
                        </section>

                        <section className="glass-card rounded-xl p-8">
                            <h2 className="text-2xl font-bold mb-4 text-cyan-400">9. Insurance</h2>
                            <p className="text-white/80 leading-relaxed mb-4">
                                Shiro Security maintains the following insurance coverage:
                            </p>
                            <ul className="list-disc list-inside text-white/80 space-y-2">
                                <li><strong>Technology Errors & Omissions (E&O) Insurance:</strong> Coverage for claims arising from AI-related decisions and algorithmic actions</li>
                                <li><strong>Cyber Liability Insurance:</strong> Coverage for data breaches and cyber incidents</li>
                                <li><strong>General Commercial Liability:</strong> Standard business liability coverage</li>
                            </ul>
                            <p className="text-white/80 leading-relaxed mt-4">
                                The Client is strongly encouraged to maintain their own cyber insurance coverage appropriate for their business operations.
                            </p>
                        </section>

                        <section className="glass-card rounded-xl p-8">
                            <h2 className="text-2xl font-bold mb-4 text-cyan-400">10. Confidentiality</h2>
                            <p className="text-white/80 leading-relaxed mb-4">
                                Both parties agree to maintain the confidentiality of the other party's Confidential Information. "Confidential Information" includes business information, technical data, security configurations, and any information marked as confidential.
                            </p>
                            <p className="text-white/80 leading-relaxed">
                                Confidentiality obligations do not apply to information that: (a) is or becomes publicly available; (b) was known prior to disclosure; (c) is independently developed; or (d) is required to be disclosed by law.
                            </p>
                        </section>

                        <section className="glass-card rounded-xl p-8">
                            <h2 className="text-2xl font-bold mb-4 text-cyan-400">11. Governing Law and Dispute Resolution</h2>
                            
                            <h3 className="text-xl font-semibold mb-3 text-white">11.1 Governing Law</h3>
                            <p className="text-white/80 leading-relaxed mb-4">
                                This Agreement shall be governed by the laws of the State of Delaware, without regard to conflict of law principles.
                            </p>

                            <h3 className="text-xl font-semibold mb-3 text-white">11.2 Arbitration</h3>
                            <p className="text-white/80 leading-relaxed mb-4">
                                Any disputes arising from this Agreement shall be resolved through binding arbitration administered by the American Arbitration Association under its Commercial Arbitration Rules.
                            </p>

                            <h3 className="text-xl font-semibold mb-3 text-white">11.3 Class Action Waiver</h3>
                            <p className="text-white/80 leading-relaxed">
                                Both parties agree to resolve disputes on an individual basis and waive any right to participate in class action lawsuits or class-wide arbitration.
                            </p>
                        </section>

                        <section className="glass-card rounded-xl p-8">
                            <h2 className="text-2xl font-bold mb-4 text-cyan-400">12. Miscellaneous</h2>
                            
                            <h3 className="text-xl font-semibold mb-3 text-white">12.1 Entire Agreement</h3>
                            <p className="text-white/80 leading-relaxed mb-4">
                                This Agreement constitutes the entire agreement between the parties and supersedes all prior agreements and understandings.
                            </p>

                            <h3 className="text-xl font-semibold mb-3 text-white">12.2 Amendment</h3>
                            <p className="text-white/80 leading-relaxed mb-4">
                                Shiro Security may modify this Agreement by providing notice to the Client. Continued use of the Services after such notice constitutes acceptance of the modified terms.
                            </p>

                            <h3 className="text-xl font-semibold mb-3 text-white">12.3 Severability</h3>
                            <p className="text-white/80 leading-relaxed mb-4">
                                If any provision is found unenforceable, the remaining provisions shall continue in effect.
                            </p>

                            <h3 className="text-xl font-semibold mb-3 text-white">12.4 Force Majeure</h3>
                            <p className="text-white/80 leading-relaxed">
                                Neither party shall be liable for failures due to circumstances beyond their reasonable control, including natural disasters, war, terrorism, or government actions.
                            </p>
                        </section>

                        <section className="glass-card rounded-xl p-8">
                            <h2 className="text-2xl font-bold mb-4 text-cyan-400">13. Contact Information</h2>
                            <p className="text-white/80 leading-relaxed">
                                For questions about this Agreement, please contact:
                            </p>
                            <div className="mt-4 text-white/80">
                                <p><strong>Shiro Security, LLC</strong></p>
                                <p>Legal Department</p>
                                <p>Email: legal@shirosec.com</p>
                                <p>Website: www.shirosec.com</p>
                            </div>
                        </section>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="py-8 px-6 border-t border-white/5">
                <div className="max-w-7xl mx-auto text-center text-white/40 text-sm">
                    &copy; {new Date().getFullYear()} Shiro Security, LLC. All rights reserved.
                </div>
            </footer>
        </div>
    );
};

export default Terms;
